<script lang="ts">
	import AuthAPI from '$lib/api/auth';
	import { Button } from '$lib/components/ui/button';
	import * as DropdownMenu from '$lib/components/ui/dropdown-menu/index';
	import { Text } from '$lib/components/ui/text';
	import { truncateString } from '$lib/helper';
	import { t } from '$lib/i18n';
	import { isDesktop, rerender } from '$lib/stores/storeCommon';
	import { storeUserInfo } from '$lib/stores/storeUser';
	import { bscChain, wagmiConfig } from '$lib/web3/client';
	import { contractConfig } from '$lib/web3/contract/contractConfig';
	import { connectWallet, onDisconnect } from '$lib/web3/wagmi';
	import Icon from '@iconify/svelte';
	import { getBalance, type GetBalanceReturnType } from '@wagmi/core';
	import { zeroAddress } from 'viem';
	import type { Address } from 'viem/accounts';
	import ConnectWallet from './ConnectWallet.svelte';

	let open = false;

	const onHandleDisconnect = async () => {
		await AuthAPI.logout();
		await onDisconnect();
	};

	let userBalance = {
		BNB: {} as GetBalanceReturnType,
		USDT: {} as GetBalanceReturnType,
		PartyTicket: {} as GetBalanceReturnType,
		// pEICToken: {} as GetBalanceReturnType,
		bEICToken: {} as GetBalanceReturnType,
		paToken: {} as GetBalanceReturnType
	};

	async function onFetchColdWalletBalance() {
		userBalance['BNB'] = await getBalance(wagmiConfig, {
			address: $storeUserInfo.web3_address,
			chainId: bscChain.id
		});
		const balancePromises = Object.entries(contractConfig).map(async ([key, value]) => {
			if (userBalance.hasOwnProperty(key)) {
				const balance = await getBalance(wagmiConfig, {
					address: $storeUserInfo.web3_address,
					token: value as Address,
					chainId: bscChain.id
				});

				// Assign the balance to the corresponding key in userBalance
				// @ts-ignore
				userBalance[key] = balance;
			}
		});

		// Wait for all balance promises to resolve
		await Promise.all(balancePromises);
	}

	$: if ($rerender || !$rerender) {
		onFetchColdWalletBalance();
	}

	storeUserInfo.subscribe((value) => {
		if (value.web3_address !== zeroAddress) {
			onFetchColdWalletBalance();
		}
	});
</script>

{#if $isDesktop}
	{#if $storeUserInfo.web3_address === zeroAddress}
		<ConnectWallet class="hidden md:block" />
	{:else}
		<DropdownMenu.Root bind:open>
			<DropdownMenu.Trigger asChild let:builder>
				<Button
					builders={[builder]}
					class="bg-[#481555] px-2 font-bold  xl:bg-[#29193D] xl:py-[10px]"
				>
					<div
						class="pixel-font flex w-full items-center gap-x-2 pl-3 text-[12px] md:pl-3 md:text-md"
					>
						{truncateString($storeUserInfo.web3_address, 4, 4)}
						<Icon
							icon="tabler:chevron-down"
							class={open ? 'rotate-180 transition' : 'rotate-0 transition'}
						/>
					</div>
				</Button>
			</DropdownMenu.Trigger>
			<DropdownMenu.Content class="w-56 border-none bg-[#251235]">
				<div class="mb-1 space-y-2 bg-black/40 p-2">
					{#each Object.entries(userBalance) as key}
						<div class="flex w-full items-center justify-between">
							<div class="flex items-center gap-x-2">
								<img src={`/img/token/${key[0]}.png`} class="w-4" alt="" />
								<Text class="text-[12px]">{key[1].symbol}</Text>
							</div>
							<Text class="text-[12px] "
								>{parseFloat(Number(key[1].formatted).toFixed(4)) || 0}</Text
							>
						</div>
					{/each}
				</div>
				<DropdownMenu.Item class="wallet-dropdown flex justify-between gap-x-2 ">
					<div class="flex gap-x-3">
						<Icon icon="solar:ranking-linear" class="text-xl" />
						<p>{$t('donate.rank')}</p>
					</div>
					<p>
						{$storeUserInfo.rank === 'none' || !$storeUserInfo.rank
							? '-'
							: $t(`home.planet.${$storeUserInfo.rank}`)}
					</p></DropdownMenu.Item
				>
				<DropdownMenu.Item
					on:click={onHandleDisconnect}
					class="wallet-dropdown flex cursor-pointer gap-x-2 "
				>
					<Icon icon="material-symbols:logout" class="text-2xl" />
					{$t('common.disconnect')}</DropdownMenu.Item
				>
			</DropdownMenu.Content>
		</DropdownMenu.Root>
	{/if}
{:else if !$isDesktop}
	<div class="mr-2 flex xl:hidden">
		{#if $storeUserInfo.web3_address === zeroAddress}
			<Button on:click={connectWallet} class="max-h-[30px] bg-[#481555] px-2 py-1 font-bold ">
				<div class="pixel-font flex w-full items-center gap-x-2 pl-2 text-[12px] md:text-md">
					<p class="pixel-font text-[12px]">{$t('common.connect_wallet')}</p>
				</div>
			</Button>
		{:else}
			<DropdownMenu.Root bind:open>
				<DropdownMenu.Trigger asChild let:builder>
					<Button builders={[builder]} class="max-h-[30px] bg-[#481555] px-2 py-1 font-bold">
						<div class="pixel-font flex w-full items-center gap-x-2 text-[12px] md:text-md">
							{truncateString($storeUserInfo.web3_address, 4, 4)}
						</div>
					</Button>
				</DropdownMenu.Trigger>
				<DropdownMenu.Content class="w-56 border-none  bg-[#251235]">
					{#if $storeUserInfo.web3_address !== zeroAddress}
						<div class="mb-1 space-y-2 rounded-lg bg-black/40 px-3 py-2">
							{#each Object.entries(userBalance) as key}
								<div class="flex w-full items-center justify-between">
									<div class="flex items-center gap-x-2">
										<img
											src={`/img/token/${key[0] === 'bEICToken' ? 'pEICToken' : key[0]}.png`}
											class="w-4"
											alt=""
										/>
										<Text class="text-[12px]">{key[1].symbol}</Text>
									</div>
									<Text class="text-[12px] "
										>{parseFloat(Number(key[1].formatted).toFixed(4)) || 0}</Text
									>
								</div>
							{/each}
						</div>
						<DropdownMenu.Item class="wallet-dropdown flex cursor-default justify-between gap-x-2">
							<div class="flex gap-x-3">
								<Icon icon="solar:ranking-linear" class="text-xl" />
								<p>{$t('donate.rank')}</p>
							</div>
							<p>
								{$storeUserInfo.rank === 'none' || !$storeUserInfo.rank
									? '-'
									: $t(`home.planet.${$storeUserInfo.rank}`)}
							</p></DropdownMenu.Item
						>
						<DropdownMenu.Item
							on:click={onHandleDisconnect}
							class="wallet-dropdown flex cursor-pointer gap-x-2 "
						>
							<Icon icon="material-symbols:logout" class="text-2xl" />
							{$t('common.disconnect')}</DropdownMenu.Item
						>
					{:else}
						<DropdownMenu.Item
							on:click={connectWallet}
							class="wallet-dropdown flex cursor-pointer gap-x-2"
						>
							<img src="/img/desktopNav/wallet.png" alt="" class=" w-4 md:w-[18px]" />
							{$t('common.connect_wallet')}</DropdownMenu.Item
						>
					{/if}
				</DropdownMenu.Content>
			</DropdownMenu.Root>
		{/if}
	</div>
{/if}
